import DownloadIcon from "@mui/icons-material/Download";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useCancelDocExtractionEvalRunMutation,
  useDocExtractionEvalRunDocumentsLazyQuery,
  useDocExtractionEvalRunQuery,
  type EvalRunDocumentFieldsFragment,
} from "../../generated/graphql";
import {
  getBackendUrlForEnv,
  readPersistedEnv,
} from "../crown-data-import/env/env-config";
import AggregateTables from "./aggregate-tables";
import { docEntityAccuracy, type FieldVerdict } from "./doc-accuracy";
import {
  docStatusColor,
  isTerminalStatus,
  runStatusColor,
} from "./eval-config";

// Lazy so the substantial pdf.js renderer is downloaded (and its module setup
// run) only when an operator opens a document, never on the run-detail's initial
// bundle for everyone visiting the page.
const EvalDocumentViewer = lazy(async () => import("./eval-document-viewer"));

const dialogTitleStyles = {
  fontFamily: "monospace",
  fontSize: 14,
} satisfies SxProps<Theme>;

const viewerFallbackStyles = {
  p: 2,
} satisfies SxProps<Theme>;

const DOC_PAGE_SIZE = 50;
const RUN_POLL_INTERVAL_MS = 5000;

const DOC_STATUSES = ["COMPLETED", "ERROR", "SKIPPED", "PENDING"] as const;
const ERROR_KINDS = [
  "provider",
  "ocr_missing",
  "gt_missing",
  "parse",
  "batch_item_failed",
] as const;

const renderValue = (value: unknown): string => {
  if (value === null || value === undefined) return "∅";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

const verdictMark = (v: boolean | null): string =>
  v === null ? "—" : v ? "✓" : "✗";

const fmtAccuracy = (verdicts: unknown): string => {
  const { correct, total, pct } = docEntityAccuracy(verdicts);
  return pct === null ? "—" : `${pct.toFixed(0)}% (${correct}/${total})`;
};

const fmtCost = (costUsd: number | null | undefined): string =>
  costUsd === null || costUsd === undefined ? "—" : `$${costUsd.toFixed(4)}`;

const VerdictRows = ({ verdicts }: { readonly verdicts: unknown }) => {
  if (verdicts === null || verdicts === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        No verdicts recorded.
      </Typography>
    );
  }
  const entries = Object.entries(verdicts as Record<string, FieldVerdict>).sort(
    ([a], [b]) => a.localeCompare(b),
  );
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Ground truth</TableCell>
          <TableCell>Extracted</TableCell>
          <TableCell align="center">Strict</TableCell>
          <TableCell align="center">Entity</TableCell>
          <TableCell align="center">M1</TableCell>
          <TableCell align="center">M2</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([field, v]) => (
          <TableRow key={field}>
            <TableCell>{field}</TableCell>
            <TableCell>{renderValue(v.groundTruth)}</TableCell>
            <TableCell>{renderValue(v.extracted)}</TableCell>
            <TableCell align="center">
              {v.skipped ? "skip" : verdictMark(v.strict)}
            </TableCell>
            <TableCell align="center">
              {v.skipped ? "skip" : verdictMark(v.entityCredit)}
            </TableCell>
            <TableCell align="center">{v.excludedM1 ? "excl" : ""}</TableCell>
            <TableCell align="center">{v.excludedM2 ? "excl" : ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const DocRow = ({
  runId,
  doc,
}: {
  readonly runId: string;
  readonly doc: EvalRunDocumentFieldsFragment;
}) => {
  const [open, setOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={open ? "Collapse verdicts" : "Expand verdicts"}
            onClick={() => {
              setOpen((v) => !v);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
          {doc.documentId}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={doc.status}
            color={docStatusColor(doc.status)}
          />
        </TableCell>
        <TableCell>{doc.errorKind ?? ""}</TableCell>
        <TableCell align="right">
          {fmtAccuracy(doc.verdicts as unknown)}
        </TableCell>
        <TableCell align="right">{fmtCost(doc.costUsd)}</TableCell>
        <TableCell align="right">{doc.inputTokens ?? "—"}</TableCell>
        <TableCell align="right">{doc.outputTokens ?? "—"}</TableCell>
        <TableCell align="right">{doc.latencyMs ?? "—"}</TableCell>
        <TableCell align="center">
          <Tooltip title="View source with OCR boxes">
            <IconButton
              size="small"
              aria-label="View document source with OCR boxes"
              onClick={() => {
                setViewerOpen(true);
              }}
            >
              <ImageSearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={10}>
          <Collapse unmountOnExit in={open} timeout="auto">
            <Box sx={{ m: 1 }}>
              {doc.errorDetail !== null && doc.errorDetail !== undefined && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {doc.errorDetail}
                </Alert>
              )}
              <VerdictRows verdicts={doc.verdicts as unknown} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Mounted only while open, so the source + word boxes are fetched lazily
          on demand (never with the row list). */}
      <Dialog
        fullWidth
        maxWidth="lg"
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
        }}
      >
        <DialogTitle sx={dialogTitleStyles}>{doc.documentId}</DialogTitle>
        <DialogContent dividers>
          {viewerOpen && (
            <Suspense
              fallback={
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={viewerFallbackStyles}
                >
                  <CircularProgress size={18} />
                  <Typography>Loading viewer…</Typography>
                </Stack>
              }
            >
              <EvalDocumentViewer runId={runId} documentId={doc.documentId} />
            </Suspense>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewerOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Full detail for one eval run: live status header, scored aggregates, and a
 * filterable, keyset-paginated per-document table with expandable field
 * verdicts. Offers a confirm-gated cancel and a raw-output JSONL export (the
 * cookie-authed REST passthrough consumed by the Python replay harness).
 */
const RunDetail = ({ runId }: { readonly runId: string }) => {
  // Override the app-wide `errorPolicy: "ignore"` so a proxy/network failure
  // surfaces as a distinct error (with retry) instead of masquerading as an
  // indefinite "Loading run…" spinner that never resolves.
  const { data, loading, error, startPolling, stopPolling, refetch } =
    useDocExtractionEvalRunQuery({
      variables: { id: runId },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
  const run = data?.docExtractionEvalRun;
  const active = run !== undefined && !isTerminalStatus(run.status);

  useEffect(() => {
    if (active) {
      startPolling(RUN_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [active, startPolling, stopPolling]);

  // Documents accumulate across keyset pages (the backend does not filter
  // server-side, so status/errorKind filtering is applied over what is loaded).
  const [docs, setDocs] = useState<EvalRunDocumentFieldsFragment[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [errorKindFilter, setErrorKindFilter] = useState<string>("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [fetchDocs, { loading: loadingDocs, error: docsError }] =
    useDocExtractionEvalRunDocumentsLazyQuery({
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
  const [cancelRun, { loading: cancelling, error: cancelError }] =
    useCancelDocExtractionEvalRunMutation();

  // How many keyset pages the operator has loaded, so an in-place refresh can
  // re-walk exactly that many pages (kept in a ref: it drives imperative
  // fetching, not rendering, so it must not retrigger effects when it changes).
  const loadedPageCount = useRef(0);

  const loadPage = useCallback(
    async (after: string | null) => {
      const res = await fetchDocs({
        variables: { runId, after: after ?? undefined, limit: DOC_PAGE_SIZE },
      });
      const page = res.data?.docExtractionEvalRunDocuments;
      if (page !== undefined && page !== null) {
        setDocs((prev) =>
          after === null ? page.documents : [...prev, ...page.documents],
        );
        setCursor(page.nextCursor ?? null);
        loadedPageCount.current =
          after === null ? 1 : loadedPageCount.current + 1;
      }
    },
    [fetchDocs, runId],
  );

  // Re-walk the already-loaded keyset chain (recursively, to avoid an awaited
  // loop) and replace the loaded docs wholesale. Used to refresh settled
  // statuses/tokens on the visible rows while the run is still live.
  const refetchLoadedChain = useCallback(
    async (
      pagesRemaining: number,
      after: string | null,
      accumulated: EvalRunDocumentFieldsFragment[],
    ): Promise<{
      docs: EvalRunDocumentFieldsFragment[];
      cursor: string | null;
    }> => {
      if (pagesRemaining <= 0) {
        return { docs: accumulated, cursor: after };
      }
      const res = await fetchDocs({
        variables: { runId, after: after ?? undefined, limit: DOC_PAGE_SIZE },
      });
      const page = res.data?.docExtractionEvalRunDocuments;
      if (page === undefined || page === null) {
        return { docs: accumulated, cursor: after };
      }
      const nextCursor = page.nextCursor ?? null;
      const merged = [...accumulated, ...page.documents];
      if (nextCursor === null) {
        return { docs: merged, cursor: null };
      }
      return refetchLoadedChain(pagesRemaining - 1, nextCursor, merged);
    },
    [fetchDocs, runId],
  );

  const refreshLoadedDocs = useCallback(async () => {
    const pages = loadedPageCount.current;
    if (pages === 0) return;
    const { docs: refreshed, cursor: nextCursor } = await refetchLoadedChain(
      pages,
      null,
      [],
    );
    setDocs(refreshed);
    setCursor(nextCursor);
  }, [refetchLoadedChain]);

  useEffect(() => {
    setDocs([]);
    setCursor(null);
    loadedPageCount.current = 0;
    void loadPage(null);
  }, [runId, loadPage]);

  // While the run is live, the header polls but the loaded document rows would
  // otherwise freeze at their first-fetched state. Re-pull the loaded pages
  // whenever the settled count or status advances so their verdicts/tokens keep
  // pace (a no-op until at least one page is loaded).
  const settledCount = (run?.completedCount ?? 0) + (run?.errorCount ?? 0);
  const runStatus = run?.status;
  useEffect(() => {
    if (!active) return;
    void refreshLoadedDocs();
  }, [settledCount, runStatus, active, refreshLoadedDocs]);

  const filteredDocs = useMemo(
    () =>
      docs.filter(
        (d) =>
          (statusFilter === "" || d.status === statusFilter) &&
          (errorKindFilter === "" || d.errorKind === errorKindFilter),
      ),
    [docs, statusFilter, errorKindFilter],
  );

  const exportUrl = `${getBackendUrlForEnv(
    readPersistedEnv(),
  )}/document-extraction-evals/runs/${runId}/raw-output.jsonl`;

  const handleCancel = async () => {
    await cancelRun({ variables: { runId } });
    setCancelOpen(false);
    await refetch();
  };

  // Download via a credentialed fetch → blob → object-URL click rather than a
  // plain <a href> navigation. The anchor navigation drops the session cookie
  // whenever the export targets a different-site backend (the env picker can),
  // and on any error status it navigates the whole tool away to the backend's
  // raw JSON error page; fetch keeps credentials explicit and surfaces the
  // failure in-place.
  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await fetch(exportUrl, { credentials: "include" });
      if (!response.ok) {
        throw new Error(
          `Export failed (${response.status} ${response.statusText}).`,
        );
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eval-run-${runId}-raw-output.jsonl`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setExporting(false);
    }
  };

  if (run === undefined) {
    if (error !== undefined) {
      return (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Failed to load run: {error.message}
        </Alert>
      );
    }
    if (loading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={18} />
          <Typography>Loading run…</Typography>
        </Stack>
      );
    }
    return <Typography sx={{ p: 2 }}>Run not found.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">{run.name}</Typography>
            <Chip
              size="small"
              label={run.status}
              color={runStatusColor(run.status)}
            />
            <Typography variant="body2" color="text.secondary">
              {run.executionMode} · {run.completedCount + run.errorCount} /{" "}
              {run.documentCount} settled · {run.errorCount} errors
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={exporting}
              onClick={() => {
                void handleExport();
              }}
            >
              {exporting ? "Exporting…" : "Export raw-output.jsonl"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={!active || run.cancelRequestedAt !== null}
              onClick={() => {
                setCancelOpen(true);
              }}
            >
              {run.cancelRequestedAt === null ? "Cancel" : "Cancel requested"}
            </Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          bundle {run.bundleHash} · actual cost{" "}
          {run.actualCostUsd !== null && run.actualCostUsd !== undefined
            ? `$${run.actualCostUsd.toFixed(2)}`
            : "—"}
        </Typography>
        {cancelError !== undefined && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Cancel failed: {cancelError.message}
          </Alert>
        )}
        {exportError !== null && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Export failed: {exportError}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography gutterBottom variant="h6">
          Aggregates
        </Typography>
        <AggregateTables aggregates={run.aggregates as unknown} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Documents
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {DOC_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="errorkind-filter-label">Error kind</InputLabel>
            <Select
              labelId="errorkind-filter-label"
              label="Error kind"
              value={errorKindFilter}
              onChange={(e) => {
                setErrorKindFilter(e.target.value);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {ERROR_KINDS.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Document</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
              <Tooltip title="Entity-credited micro accuracy over this document's non-skipped fields (correct/total).">
                <TableCell align="right">Accuracy</TableCell>
              </Tooltip>
              <Tooltip title="This document's recorded token usage priced by the run model.">
                <TableCell align="right">Cost</TableCell>
              </Tooltip>
              <TableCell align="right">In tok</TableCell>
              <TableCell align="right">Out tok</TableCell>
              <TableCell align="right">Latency</TableCell>
              <TableCell align="center">Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocs.map((doc) => (
              <DocRow key={doc.documentId} runId={runId} doc={doc} />
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            disabled={cursor === null || loadingDocs}
            onClick={() => {
              void loadPage(cursor);
            }}
          >
            {loadingDocs
              ? "Loading…"
              : cursor === null
                ? "All loaded"
                : "Load more"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredDocs.length} of {docs.length} loaded
          </Typography>
        </Stack>

        {docsError !== undefined && (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void loadPage(cursor);
                }}
              >
                Retry
              </Button>
            }
          >
            Failed to load documents: {docsError.message}. Already-loaded rows
            are unaffected.
          </Alert>
        )}
      </Paper>

      <Dialog
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
        }}
      >
        <DialogTitle>Cancel this run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancelling stops further document processing. Any results already
            scored are kept. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelOpen(false);
            }}
          >
            Keep running
          </Button>
          <Button color="error" disabled={cancelling} onClick={handleCancel}>
            {cancelling ? "Cancelling…" : "Cancel run"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default RunDetail;
